import dayjs from 'dayjs';
import { computed } from 'vue';

import { CUSTOMER_PLAN } from '@/models/auth/customer-plan.enum';
import { useCustomerJourneyStore } from '@/stores/customer-journey';

export function useTrialStates() {
  function trialActiveOrExpiredForTwoDays(trial) {
    const endDate = dayjs(trial.trialEndDate).add(2, 'days');
    const today = dayjs();
    return endDate >= today;
  }

  function getTrialDaysLeft(trial) {
    const trialEndDate = dayjs(trial?.trialEndDate);
    const today = dayjs();
    return Math.ceil(trialEndDate.diff(today, 'days', true));
  }

  const customerJourneyStore = useCustomerJourneyStore();

  const showPlanTrial = computed(
    () =>
      customerJourneyStore.additionalPlanTrial?.trialEndDate &&
      trialActiveOrExpiredForTwoDays(customerJourneyStore.additionalPlanTrial),
  );

  const showPremiumAnalyticsTrial = computed(
    () =>
      customerJourneyStore.additionalPremiumAnalyticsTrial?.trialEndDate &&
      trialActiveOrExpiredForTwoDays(customerJourneyStore.additionalPremiumAnalyticsTrial),
  );

  const showCreatorManagementTrial = computed(
    () =>
      customerJourneyStore.addOnCreatorManagementTrial?.trialEndDate &&
      trialActiveOrExpiredForTwoDays(customerJourneyStore.addOnCreatorManagementTrial),
  );

  const showSocialListeningTrial = computed(
    () =>
      customerJourneyStore.socialListeningTrial?.trialEndDate &&
      trialActiveOrExpiredForTwoDays(customerJourneyStore.socialListeningTrial),
  );

  const planTrialType = computed(() => customerJourneyStore.additionalPlanTrial?.dhProduct?.name);

  const planTrialDaysLeft = computed(() =>
    getTrialDaysLeft(customerJourneyStore.additionalPlanTrial),
  );

  const premiumAnalyticsTrialDaysLeft = computed(() =>
    getTrialDaysLeft(customerJourneyStore.additionalPremiumAnalyticsTrial),
  );

  const creatorManagementTrialDaysLeft = computed(() =>
    getTrialDaysLeft(customerJourneyStore.addOnCreatorManagementTrial),
  );

  const socialListeningTrialDaysLeft = computed(() =>
    getTrialDaysLeft(customerJourneyStore.socialListeningTrial),
  );

  const basePlanTrialDaysLeft = computed(() =>
    getTrialDaysLeft(customerJourneyStore.basePlanTrial),
  );

  function getBrandTrialDaysLeft() {
    const earliestExpiration =
      planTrialDaysLeft.value > basePlanTrialDaysLeft.value &&
      customerJourneyStore.basePlanTrial?.dhProduct?.name === CUSTOMER_PLAN.GROW.name
        ? basePlanTrialDaysLeft.value
        : planTrialDaysLeft.value;
    return showPlanTrial.value && earliestExpiration > 0
      ? earliestExpiration
      : premiumAnalyticsTrialDaysLeft.value;
  }

  function getBrandTrialProductCopy() {
    if (planTrialDaysLeft.value > 0 && premiumAnalyticsTrialDaysLeft.value <= 0) {
      return planTrialType.value;
    }
    if (premiumAnalyticsTrialDaysLeft.value > 0 && planTrialDaysLeft.value <= 0) {
      return 'Premium Analytics';
    }
    return `${showPlanTrial.value ? planTrialType.value : ''}${showPlanTrial.value && showPremiumAnalyticsTrial.value ? ' + ' : ''}${showPremiumAnalyticsTrial.value ? 'Premium Analytics' : ''}`;
  }

  const activeTrialsInfo = computed(() => {
    const activeTrials = [];
    if (planTrialDaysLeft.value > 0 || premiumAnalyticsTrialDaysLeft.value > 0) {
      activeTrials.push({
        product: getBrandTrialProductCopy(),
        daysLeft: getBrandTrialDaysLeft(),
      });
    } else if (basePlanTrialDaysLeft.value > 0) {
      activeTrials.push({
        product: customerJourneyStore.basePlanTrial.dhProduct.name,
        daysLeft: basePlanTrialDaysLeft.value,
      });
    }
    if (creatorManagementTrialDaysLeft.value > 0) {
      activeTrials.push({
        product: customerJourneyStore.addOnCreatorManagementTrial?.dhProduct?.name,
        daysLeft: creatorManagementTrialDaysLeft.value,
      });
    }
    if (socialListeningTrialDaysLeft.value > 0) {
      activeTrials.push({
        product: 'Social Listening',
        daysLeft: socialListeningTrialDaysLeft.value,
      });
    }
    return activeTrials;
  });

  const expiredTrialsInfo = computed(() => {
    const expiredTrials = [];
    if (
      ((showPlanTrial.value && planTrialDaysLeft.value <= 0) ||
        (showPremiumAnalyticsTrial.value && premiumAnalyticsTrialDaysLeft.value <= 0)) &&
      !(planTrialDaysLeft.value > 0 || premiumAnalyticsTrialDaysLeft.value > 0)
    ) {
      expiredTrials.push(getBrandTrialProductCopy());
    }
    if (showCreatorManagementTrial.value && creatorManagementTrialDaysLeft.value <= 0) {
      expiredTrials.push(customerJourneyStore.addOnCreatorManagementTrial?.dhProduct?.name);
    }
    if (showSocialListeningTrial.value && socialListeningTrialDaysLeft.value <= 0) {
      expiredTrials.push('Social Listening');
    }
    return expiredTrials;
  });

  const expansionTrialDaysLeft = computed(
    () => (trialInfo) =>
      trialInfo.daysLeft > 1
        ? `<span>${trialInfo.daysLeft}</span> days remaining in your <span class="font-medium">${trialInfo.product}</span> trial.`
        : `This is the last day of your <span class="font-medium">${trialInfo.product}</span> trial.`,
  );

  return {
    socialListeningTrialDaysLeft,
    basePlanTrialDaysLeft,
    activeTrialsInfo,
    expiredTrialsInfo,
    expansionTrialDaysLeft,
    trialActiveOrExpiredForTwoDays,
    showPlanTrial,
    showPremiumAnalyticsTrial,
    showCreatorManagementTrial,
    getTrialDaysLeft,
  };
}
